// Extracts a canonical US state name from a listing's free-text
// location field. Two real formats exist in the data:
//   - "City, ST" or "City (Suburb), ST"  -- FXG's dominant format
//   - "Central Alabama" / "Delaware"      -- Route Consultant's format
// Verified against 100 real distinct location values from production
// data: 99/100 matched (the one miss was a truncated log artifact, not
// a real DB value). Also correctly collapses format duplicates like
// "Baton Rouge, LA" and "Baton Rouge, Louisiana" to the same state.

const STATE_ABBREVIATIONS: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
  HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
  KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire",
  NJ: "New Jersey", NM: "New Mexico", NY: "New York", NC: "North Carolina",
  ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota", TN: "Tennessee",
  TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

const US_STATES = Object.values(STATE_ABBREVIATIONS);
const SORTED_STATE_NAMES = [...US_STATES].sort((a, b) => b.length - a.length);

// All 50 states, alphabetical -- for the state filter dropdown, which
// should always show every state regardless of whether any CURRENT
// listing happens to be in it (a listing for a previously-empty state
// could appear tomorrow).
export const ALL_US_STATES = [...US_STATES].sort();

export function extractState(location: string | null | undefined): string | null {
  if (!location) return null;

  // "City, ST" or "City (Suburb #), ST" -- trailing 2-letter abbreviation
  const abbrevMatch = location.match(/,\s*([A-Z]{2})(?:\s*\(\d+\))?\s*$/);
  if (abbrevMatch) {
    const abbrev = abbrevMatch[1];
    if (STATE_ABBREVIATIONS[abbrev]) return STATE_ABBREVIATIONS[abbrev];
  }

  // Regional description ("Central Alabama") or a bare state name
  // ("Delaware"). Longest-name-first avoids a short state name
  // false-matching as a substring of a longer one.
  for (const state of SORTED_STATE_NAMES) {
    const re = new RegExp(`\\b${state}\\b`, "i");
    if (re.test(location)) return state;
  }

  return null;
}
